import type { IProjectModel } from "@models/project/IProjectModel";
import type { EntitySnapshot, EntityTransform } from "@models/data/entityData";
import type { IPlacementGeometryHost, IPlacementModel } from "./IPlacementModel";

/**
 * Web port of the first useful `PlacementModel` actions. It delegates all
 * persistence to `IProjectModel` so inspector buttons and future gizmo drags
 * share the same write path.
 */
export class PlacementModel implements IPlacementModel {
  private readonly m_project: IProjectModel;
  private m_geometryHost: IPlacementGeometryHost | null = null;

  constructor(project: IProjectModel) {
    this.m_project = project;
  }

  attachGeometryHost(host: IPlacementGeometryHost | null): void {
    this.m_geometryHost = host;
  }

  async placeOnGroundAsync(sceneId: string, snapshotId: string): Promise<void> {
    const position = this.m_geometryHost?.calculateGroundedPosition(sceneId, snapshotId);
    if (!position) return;
    await this.m_project.updateEntitySnapshotAsync(sceneId, snapshotId, {
      transform: { position },
    });
  }

  async resetPositionAsync(sceneId: string, snapshotId: string): Promise<void> {
    const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
    if (!snapshot) return;
    const resetTransform = getResetTransform(snapshot);
    await this.m_project.updateEntitySnapshotAsync(sceneId, snapshotId, {
      transform: { position: resetTransform.position },
    });
  }

  async resetRotationAsync(sceneId: string, snapshotId: string): Promise<void> {
    const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
    if (!snapshot) return;
    const resetTransform = getResetTransform(snapshot);
    await this.m_project.updateEntitySnapshotAsync(sceneId, snapshotId, {
      transform: { rotation: resetTransform.rotation },
    });
  }

  async resetTransformAsync(sceneId: string, snapshotId: string): Promise<void> {
    const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
    if (!snapshot) return;
    const resetTransform = getResetTransform(snapshot);
    await this.m_project.updateEntitySnapshotAsync(sceneId, snapshotId, {
      transform: {
        position: resetTransform.position,
        rotation: resetTransform.rotation,
      },
    });
  }

  async resetScaleAsync(sceneId: string, snapshotId: string): Promise<void> {
    const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
    if (!snapshot) return;
    // Unity `SelectedGameObjectModel.ResetScale` → `GetOriginalScale`: the
    // original imported `PartData` scale, not a hardcoded [1,1,1] (the
    // snapshot's resetTransform carries it, with a [1,1,1] fallback).
    await this.m_project.updateEntitySnapshotAsync(sceneId, snapshotId, {
      transform: { scale: getResetTransform(snapshot).scale },
    });
  }

  async alignToCadOriginsAsync(sceneId: string, snapshotId: string): Promise<void> {
    await this.resetTransformAsync(sceneId, snapshotId);
  }
}

function getResetTransform(snapshot: EntitySnapshot): EntityTransform {
  return snapshot.resetTransform ?? snapshot.transform;
}
